// src/lib/useTheme.ts
import { useEffect, useMemo, useState } from "react"

type Theme = "light" | "dark" | "system"
const KEY = "theme"
const EVT = "app-theme-change"

let globalTheme: Theme | null = null

function prefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

function updateDom(theme: Theme) {
  const eff = theme === "system" ? (prefersDark() ? "dark" : "light") : theme
  document.documentElement.classList.toggle("dark", eff === "dark")
}

function getInitialTheme(): Theme {
  try {
    return (localStorage.getItem(KEY) as Theme) || "system"
  } catch {
    return "system"
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => globalTheme ?? getInitialTheme())

  const effective: "light" | "dark" = useMemo(() => {
    if (theme !== "system") return theme
    return prefersDark() ? "dark" : "light"
  }, [theme])

  // apply on mount & whenever theme changes locally
  useEffect(() => {
    updateDom(theme)
    try {
      localStorage.setItem(KEY, theme)
    } catch (e) {
      console.error(e)
    }
  }, [theme])

  // subscribe to global/app + storage + system changes
  useEffect(() => {
    const onGlobal = (e: Event) => {
      const t = (e as CustomEvent).detail as Theme
      setThemeState(t)
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY && e.newValue) setThemeState(e.newValue as Theme)
    }
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    const onMq = () => theme === "system" && setThemeState("system") // recompute effective

    window.addEventListener(EVT, onGlobal as EventListener)
    window.addEventListener("storage", onStorage)
    mq.addEventListener("change", onMq)

    return () => {
      window.removeEventListener(EVT, onGlobal as EventListener)
      window.removeEventListener("storage", onStorage)
      mq.removeEventListener("change", onMq)
    }
  }, [theme])

  const setTheme = (t: Theme) => {
    globalTheme = t
    setThemeState(t)
    try {
      localStorage.setItem(KEY, t)
    } catch (e) {
      console.error(e)
    }
    updateDom(t)
    window.dispatchEvent(new CustomEvent(EVT, { detail: t }))
  }

  return { theme, setTheme, effective }
}
